import { isElementOverflowing } from "./miscellaneous";
import { useState, useEffect, useCallback } from "react";
import {
  FormSchemaBuilderErrors,
  ErrorMessages,
} from "../components/Fields/types/fieldTypes";
import { lowerFirst, upperFirst } from "lodash";

export const useOverflowTooltip = (
  element: HTMLElement | undefined | null,

  // Frankly, we do not care what type the input value is. This is only used for signaling.
  inputValue: any
) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false);

  const handleInputResize = useCallback(() => {
    if (!!element) {
      setIsTooltipVisible(isElementOverflowing(element));
    }
  }, [element, setIsTooltipVisible]);

  useEffect(() => {
    if (!element) {
      return;
    }

    // check overflow when component mounts - pretty much always does nothing now, because the element is not rendered yet
    handleInputResize();

    // ResizeObserver is more performant than window.addEventListener("resize", handleInputResize);
    // It also captures more changes of interest, including layout changes, I think.
    const observer = new ResizeObserver(handleInputResize);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [handleInputResize, element]);

  //check overflow whenever the text changes
  useEffect(() => {
    handleInputResize();
  }, [inputValue, handleInputResize]);

  return isTooltipVisible;
};

export const getErrorMessagesFromFormSchemaBuilderErrors = (
  formSchemaBuilderErrors?: FormSchemaBuilderErrors,
  fieldKey?: string,
  fieldPath?: string
): ErrorMessages => {
  if (!formSchemaBuilderErrors || !fieldKey) {
    return [];
  }

  const errorMessages: ErrorMessages = [];
  const possibleKeys = [lowerFirst(fieldKey), upperFirst(fieldKey)];

  for (const key of Object.keys(formSchemaBuilderErrors)) {
    for (const possibleKey of possibleKeys) {
      if (key === possibleKey) {
        errorMessages.push(...formSchemaBuilderErrors[key]);
      } else if (key.startsWith(possibleKey + ".")) {
        const subKey = key.slice(key.indexOf(".") + 1);
        errorMessages.push({
          fieldKey: subKey,
          errorMessages: formSchemaBuilderErrors[key],
          [Symbol.iterator]: function (): StringIterator<string> {
            throw new Error("You cannot render a `FieldErrorMessages`.");
          },
        });
      } else if (
        fieldPath &&
        fieldPath.toLowerCase().includes(key.toLowerCase())
      ) {
        // Get the error message for the fieldPath considering the case sensitivity
        // Create newe version of formSchemaBuilderErrors where all the keys are lowercased
        const newFormSchemaBuilderErrors = { ...formSchemaBuilderErrors };
        for (const key of Object.keys(newFormSchemaBuilderErrors)) {
          newFormSchemaBuilderErrors[key.toLowerCase()] =
            newFormSchemaBuilderErrors[key];
        }
        // Get the error message for the fieldPath considering the case sensitivity
        const fieldPathErrorMessages =
          newFormSchemaBuilderErrors[fieldPath.toLowerCase()];
        if (fieldPathErrorMessages) {
          // If not exists, add the error message
          errorMessages.push(...fieldPathErrorMessages);
        }
      }
    }
  }

  // Clean Messages. Remove duplicates and empty messages when key is the same
  let cleanedErrorMessages: ErrorMessages = [];
  for (const err of errorMessages) {
    // string should not be duplicated neither
    if (
      typeof err === "string" &&
      cleanedErrorMessages.some((e) => e === err)
    ) {
      continue;
    }
    // if the error message is a string, it should not be duplicated
    if (typeof err === "string") {
      cleanedErrorMessages.push(err);
      continue;
    }

    cleanedErrorMessages.push({
      ...err,
      errorMessages: Array.from(new Set(err.errorMessages)),
    });
  }

  return cleanedErrorMessages;
};

export const getErrorMessagesFromErrorMessages = (
  errorMessages?: ErrorMessages,
  fieldKey?: string
): ErrorMessages => {
  if (!errorMessages || !fieldKey) {
    return [];
  }

  const messages: ErrorMessages = [];

  const possibleKeys = [lowerFirst(fieldKey), upperFirst(fieldKey)];

  for (const possibleKey of possibleKeys) {
    for (const errorMessage of errorMessages) {
      if (typeof errorMessage === "string") {
        // We've been asked for an error message with a specific `fieldKey`, so we ignore those that do not have one.
      } else if (errorMessage.fieldKey === possibleKey) {
        messages.push(...errorMessage.errorMessages);
      } else if (errorMessage.fieldKey.startsWith(possibleKey + ".")) {
        const subKey = errorMessage.fieldKey.slice(
          errorMessage.fieldKey.indexOf(".") + 1
        );
        messages.push({
          fieldKey: subKey,
          errorMessages: errorMessage.errorMessages,
          [Symbol.iterator]: function (): StringIterator<string> {
            throw new Error("You cannot render a `FieldErrorMessages`.");
          },
        });
      }
    }
  }

  return messages;
};
